import React from 'react';
import { Alert, Textarea } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import { REASON_MAX, REASON_MIN } from './shared';

/**
 * Suspend one doctor's public page (design §D5, §8.8).
 *
 * The reason is MANDATORY: the doctor reads it, read-only, in «صفحهٔ من», and it
 * is the only record of why their page vanished. The BFF refuses an empty one
 * too; this dialog simply never sends it. Built on `TaskDialog`, so the dialog
 * stays open with every keystroke on a failed write, cannot double-submit, and
 * asks before discarding a typed reason (DESIGN_CONCEPT §2.4, §5.2).
 *
 * The confirm is destructive-styled and VERB-labelled («تعلیق کن»).
 */
export default function SuspendPageDialog({
  page, pending, error, onOpenChange, onSubmit,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => { setReason(''); setTouched(false); }, [page?.pageId]);

  const trimmed = reason.trim();
  const invalid = trimmed.length < REASON_MIN;
  const name = page?.displayName || page?.slug || '';

  return (
    <TaskDialog
      open={!!page}
      onOpenChange={onOpenChange}
      title={t('doctorPages.suspend.title', { name })}
      error={error}
      pending={pending}
      dirty={!!reason}
      submitLabel={t('doctorPages.suspend.submit')}
      submitVariant="destructive"
      submitDisabled={invalid}
      onSubmit={() => { setTouched(true); if (!invalid) onSubmit({ reason: trimmed }); }}
      data-testid="doctor-pages-suspend"
      lead={<Alert variant="error">{t('doctorPages.suspend.warning')}</Alert>}
    >
      <div data-testid="doctor-pages-suspend-reason">
        <Textarea
          label={t('doctorPages.suspend.reason')}
          required
          helperText={t('doctorPages.suspend.reasonHelp')}
          value={reason}
          onChange={(e) => setReason(e.target?.value ?? e)}
          onBlur={() => setTouched(true)}
          placeholder={t('doctorPages.suspend.reasonPlaceholder')}
          error={touched && reason && invalid ? t('validation.tooShort', { min: REASON_MIN }) : undefined}
          maxLength={REASON_MAX}
          showCount
          rows={3}
          disabled={pending}
        />
      </div>
    </TaskDialog>
  );
}
